import type { ReactElement } from 'react';
import { useCallback, useEffect, useId, useRef, useState } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import { Button } from 'platform/atoms';
import { useWriteMode } from 'platform/components/useWriteMode';
import { useImpersonatedCustomer, useLastBlockedMutation } from 'platform/readOnlyMutations';
import { useToast } from 'shared/hooks/useToast';

/**
 * A blocked write is a silent no-op otherwise: the control moves, nothing saves, and the employee
 * finds out at the next step when the value reverts.
 */
const useBlockedWriteToast = () => {
  const lastBlocked = useLastBlockedMutation();
  // Error styling, matching how a genuine save failure already reads, so "did not save" looks the
  // same whether the write was blocked or the request failed.
  const { showToast } = useToast({
    type: 'error',
    analyticsName: 'impersonation-write-blocked',
  });
  const reported = useRef(0);

  useEffect(() => {
    if (!lastBlocked || lastBlocked.count === reported.current) {
      return;
    }
    reported.current = lastBlocked.count;
    showToast({
      message: `${lastBlocked.operationName} was not saved. Turn on write mode to make changes.`,
    });
  }, [lastBlocked, showToast]);
};

const SESSION_KEY_PREFIX = 'cake-impersonation-collapsed:';

// An absent key means collapsed, so the two states are stored explicitly.
const COLLAPSED = '1';
const EXPANDED = '0';

/**
 * Scoped to the tab session and to the customer being viewed, so opening it for one customer says
 * nothing about the next. Storage throws in private mode, where the cost is only that the notice
 * returns to its default on the next page.
 */
const useCollapsed = (storageKey: string): [boolean, () => void] => {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return window.sessionStorage.getItem(storageKey) !== EXPANDED;
    } catch {
      return true;
    }
  });

  const toggle = useCallback(() => {
    const next = !collapsed;
    setCollapsed(next);
    try {
      window.sessionStorage.setItem(storageKey, next ? COLLAPSED : EXPANDED);
    } catch {
      // Losing the write only costs the remembered state, per the note above.
    }
  }, [collapsed, storageKey]);

  return [collapsed, toggle];
};

// The inactive segment sits directly on the track, so `!` suppresses the background the button
// variant paints for itself. Its text has to contrast the card tone underneath, which is what
// `blocked` selects. One shared width keeps the active segment a constant-size thumb as it moves.
const segmentClass = (active: boolean, blocked: boolean) =>
  cn(
    'h-7 w-14 shrink-0 rounded-full border-0 px-2 text-label-sm transition-colors duration-200 motion-reduce:transition-none',
    active && '!bg-od-bg-primary !text-od-content-primary shadow-sm',
    !active && '!bg-transparent',
    !active && (blocked ? '!text-od-content-primary' : '!text-od-content-inverse-primary'),
  );

const BannerContent = ({ customerUUID }: { customerUUID: string }): ReactElement => {
  const { blocked, viewing, requestWriteMode, turnOffWriteMode, confirmDialog } = useWriteMode();
  const [collapsed, toggleCollapsed] = useCollapsed(`${SESSION_KEY_PREFIX}${customerUUID}`);
  const explanationId = useId();

  useBlockedWriteToast();

  const tone = blocked ? 'bg-amber-300' : 'bg-od-bg-error';
  const textTone = blocked ? 'text-od-content-primary' : 'text-od-content-inverse-primary';

  return (
    <>
      {/* Sits over the header band, centered because every cake header is `justify-between` and
          leaves the middle free. Overlaying can cover a focused control, accepted here because
          the surface is internal only. */}
      <Box className="fixed top-2 left-1/2 z-5000 w-max max-w-full -translate-x-1/2">
        <Col
          className={cn(
            // One radius and one horizontal padding across both states, so opening the notice
            // neither snaps the corners nor shifts the toggle. The radius reads as a full pill
            // while collapsed because the browser clamps it to half the height.
            'gap-2 rounded-3xl p-2 pl-4 shadow-lg transition-[padding] duration-200 motion-reduce:transition-none',
            tone,
            !collapsed && 'gap-3 pb-3',
          )}
        >
          <Row className="items-center gap-2">
            {/* Capped so a long name cannot stretch the pill across the header. Below `sm` it
              gives up its space to the logo and the menu button, where the toggle alone still
              carries the mode. */}
            <Text
              as="p"
              role="status"
              className={cn('min-w-0 max-w-64 truncate text-label-md max-sm:sr-only', textTone)}
            >
              Impersonating {viewing}
              {/* Visually redundant with the toggle, but a screen reader gets the mode in the same
                announcement as the name rather than having to reach the buttons. */}
              <Text as="span" className="sr-only">
                {!blocked && ' in write mode'}
              </Text>
            </Text>

            <Row
              role="group"
              aria-label="Impersonation write mode"
              className="shrink-0 items-center rounded-full bg-od-bg-scrim/15 p-0.5"
            >
              <Button
                variant="secondary"
                size="sm"
                className={segmentClass(blocked, blocked)}
                analyticsName="impersonation-pill-read-only"
                aria-label="Read"
                aria-pressed={blocked}
                onClick={turnOffWriteMode}
              >
                Read
              </Button>
              <Button
                variant="secondary"
                size="sm"
                className={segmentClass(!blocked, blocked)}
                analyticsName="impersonation-pill-write-mode-on"
                aria-label="Write"
                aria-pressed={!blocked}
                onClick={() => {
                  if (blocked) {
                    requestWriteMode();
                  }
                }}
              >
                Write
              </Button>
            </Row>

            <Button
              variant="secondary"
              size="sm"
              className="size-7 shrink-0 rounded-full border-0 bg-transparent p-0"
              analyticsName={
                collapsed ? 'impersonation-pill-expand' : 'impersonation-pill-collapse'
              }
              aria-label={
                collapsed ? 'Expand the impersonation notice' : 'Collapse the impersonation notice'
              }
              aria-expanded={!collapsed}
              // Collapsed is the resting state, so a permanent `aria-controls` would spend most
              // of its life pointing at an id that is not in the document.
              aria-controls={collapsed ? undefined : explanationId}
              onClick={toggleCollapsed}
            >
              {collapsed ? <IconChevronDown size={16} /> : <IconChevronUp size={16} />}
            </Button>
          </Row>

          {/* Outside the status region above, so collapsing does not re-announce. `w-0 min-w-full`
            takes the caption row's width without contributing to it, so the sentence wraps inside
            the pill rather than widening it. */}
          {!collapsed && (
            <Box className="cake-slide-out w-0 min-w-full">
              <Text as="p" id={explanationId} className={cn('pr-2 text-body-sm', textTone)}>
                {blocked
                  ? 'This is their real dashboard. Nothing you change here is saved.'
                  : 'Everything you change saves to their real account.'}
              </Text>
            </Box>
          )}
        </Col>
      </Box>

      {confirmDialog}
    </>
  );
};

/**
 * Fixed to the top of the viewport so it stays reachable on every page, whatever that page's
 * header does on scroll.
 *
 * Remounted per customer so state from one customer cannot carry onto the next.
 */
export const ImpersonationBanner = (): ReactElement | null => {
  const impersonatedCustomer = useImpersonatedCustomer();

  if (impersonatedCustomer === null) {
    return null;
  }

  return <BannerContent key={impersonatedCustomer} customerUUID={impersonatedCustomer} />;
};
